// U32b (29/10) — ô 7 «Prompt & Video» của bảng: MỘT bước thay cho bước 7 Prompt + bước 8 Video, bố cục 3 cột 3 cột.
//   Trái  = danh sách video nhóm theo cảnh (✓ ⏳ ✕ ! ○), giữ chọn qua `?event=`.
//   Giữa  = prompt của video đang chọn: dải chip ref gửi kèm + trình sửa prompt có sẵn (`WorkflowPromptStep`: Lưu, Dựng lại/Bản máy, kiểu prompt, xem bản gửi engine). Sửa tay vẫn là EDITED như cũ.
//   Phải  = player 9:16 + dải bản clip (★ chọn bản) + thông số engine (`WorkflowGenerationEditor`, «Lưu engine») + nút Gen (runner «Gen video này») + menu ⋯ (tải clip lên · cắt đầu/đuôi · @Video · chi tiết đầy đủ).
// Không phụ thuộc duyệt Prompt: Gen một video chỉ cần prompt của CHÍNH video đó sẵn (BE chặn nếu thiếu). Logic ở promptVideoUtils (thuần).
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Popover, Spin } from 'antd';
import type { EventPlan, FilmEvent } from '../../types/film';
import type { WorkflowEventStatus, WorkflowStatus } from '../../types/filmWorkflow';
import type { PromptPick } from '../../types/filmStepReview';
import { clipSelectModeOf } from './shotCameraUtils';
import { selectedClipUrlOf, clipCardsOf } from './videoStepRowUtils';
import { genButtonOf, promptChipsOf, resolveSelectedVideoOf, videoListStatusOf } from './promptVideoUtils';
import { buildRefChoices } from './promptChipUtils';
import { videoRefOf } from './workflowL6Utils';
import { ClipInfoBlock, ClipPlayer, RefChipStrip, VersionStrip, VideoList } from './WorkflowPromptVideoParts';
import WorkflowPromptStep from './WorkflowPromptStep';
import WorkflowStepReviewCard from './WorkflowStepReviewCard';
import WorkflowGenerationEditor from './WorkflowGenerationEditor';
import WorkflowVideoL6Panel from './WorkflowVideoL6Panel';
import WorkflowRunButton from './WorkflowRunButton';
import WorkflowClipDetailDrawer from './WorkflowClipDetailDrawer';
import WfIconButton from './WfIconButton';
import { formatClock } from './workflowBoardUtils';
import { PR_REVIEW } from './stepReviewPolicy';

interface Props {
  status: WorkflowStatus;
  planId: string;
  storyId: string;
  /** Các video của plan đã sắp (cảnh → video). */
  events: WorkflowEventStatus[];
  detail: EventPlan | null;
  detailLoading: boolean;
  detailError: string | null;
  onLoadDetail: () => void;
  eventsById: Map<string, { ev: FilmEvent; sceneId: string }>;
  /** `?event=` hiện tại (có thể null / sai ⇒ video đầu tiên cần xử lý). */
  requestedId: string | null;
  onSelectVideo: (eventId: string) => void;
  busyClipId: string | null;
  onSelectClip: (eventId: string, clipId: string) => void;
  onActionDone: () => void;
}

const WorkflowPromptVideoStep: React.FC<Props> = ({
  status, planId, storyId, events, detail, detailLoading, detailError, onLoadDetail, eventsById, requestedId, onSelectVideo, busyClipId, onSelectClip, onActionDone,
}) => {
  const evOf = (id: string): FilmEvent | null => eventsById.get(id)?.ev ?? null;
  const selectedId = resolveSelectedVideoOf(events, requestedId, evOf);
  const event = events.find((e) => e.eventId === selectedId) ?? null;
  const ev = selectedId ? evOf(selectedId) : null;
  const st = event ? videoListStatusOf(event, ev) : null;

  const [focusClip, setFocusClip] = useState<string | null>(null);
  const [promptPick, setPromptPick] = useState<PromptPick | null>(null);
  const [drawer, setDrawer] = useState(false);
  useEffect(() => { setFocusClip(null); setPromptPick(null); setDrawer(false); }, [selectedId]);

  // Mở bước mà URL chưa nói video nào ⇒ ghi video mặc định lên `?event=` để F5 / chia sẻ liên kết giữ đúng chỗ.
  useEffect(() => { if (selectedId && selectedId !== requestedId && detail) onSelectVideo(selectedId); }, [selectedId, requestedId, detail]); // eslint-disable-line react-hooks/exhaustive-deps

  const choices = useMemo(() => {
    const vref = videoRefOf(ev);
    return buildRefChoices(detail?.refBank, detail?.plan.engine, vref ? [{ kind: 'video' as const, id: vref.key, label: 'Video tham chiếu', previewUrl: null, ready: true }] : []);
  }, [detail, ev]);
  const chips = useMemo(() => promptChipsOf(ev?.authorPrompt, choices), [ev?.authorPrompt, choices]);

  if (!event || !st) return <p className="muted" data-testid="wf-pv-empty-plan">Plan chưa có video nào.</p>;

  const thumb = event.card?.thumb;
  const extra = thumb?.url ? { label: thumb.kind === 'MAP' ? 'Sơ đồ định vị' : 'Storyboard', url: thumb.url } : null;
  const gen = genButtonOf(st);
  const cards = clipCardsOf(ev);
  const playUrl = (focusClip && cards.find((c) => c.id === focusClip)?.url) || selectedClipUrlOf(event, ev) || cards.find((c) => c.canPlay)?.url || null;
  const viaDrawer = clipSelectModeOf(event) === 'DRAWER';
  const onPick = (clipId: string) => { if (viaDrawer) setDrawer(true); else onSelectClip(event.eventId, clipId); };
  const moreMenu = ev ? (
    <div className="wf-pv-more" data-testid="wf-pv-more">
      <WorkflowVideoL6Panel storyId={storyId} eventId={event.eventId} eventOrder={event.eventOrder} ev={ev} onActionDone={onActionDone} />
      <Button size="small" block onClick={() => setDrawer(true)} data-testid="wf-pv-open-detail">Chi tiết clip đầy đủ (AI chấm, xoá bản…)</Button>
    </div>
  ) : null;

  return (
    <div className="wf-pv" data-testid="wf-pv">
      <VideoList events={events} selectedId={selectedId} evOf={evOf} onSelect={onSelectVideo} />

      <section className="wf-pv-mid" aria-label={`Prompt của Video ${event.eventOrder}`} data-testid="wf-pv-mid">
        <h3 className="wf-pv-h">Video {event.eventOrder}<span className="muted"> · Cảnh {event.sceneOrder}{event.genDurationS ? ` · ${formatClock(event.genDurationS)}` : ''}</span></h3>
        {!ev ? (
          detailError
            ? <Alert type="warning" showIcon message={detailError} action={<Button size="small" onClick={onLoadDetail}>Tải lại</Button>} />
            : <div style={{ padding: 12 }}><Spin size="small" /> <span className="muted">Đang tải prompt…</span></div>
        ) : (
          <>
            <RefChipStrip chips={chips} extra={extra} />
            <WorkflowPromptStep key={event.eventId} planId={planId} storyId={storyId} stepState={event.steps.PROMPT ?? 'NOT_READY'} jobs={status.jobs} failures={status.failures}
              onActionDone={onActionDone} onJobsRefresh={async () => { onActionDone(); return { jobs: status.jobs, failures: status.failures }; }}
              promptPick={promptPick} onClosePromptPick={() => setPromptPick(null)} eventId={event.eventId} ev={ev} detail={detail} />
            <details className="wf-pv-review">
              <summary className="muted">Phiếu AI chấm prompt</summary>
              <WorkflowStepReviewCard key={event.eventId} planId={planId} eventId={event.eventId} stepKey={PR_REVIEW} jobs={status.jobs} onActionDone={onActionDone} onPickEvidence={setPromptPick} />
            </details>
          </>
        )}
      </section>

      <section className="wf-pv-right" aria-label={`Clip của Video ${event.eventOrder}`} data-testid="wf-pv-right">
        <ClipPlayer url={playUrl} st={st} />
        <ClipInfoBlock ev={ev} focusId={focusClip} />
        <VersionStrip eventId={event.eventId} order={event.eventOrder} ev={ev} focusId={focusClip} busyClipId={busyClipId} viaDrawer={viaDrawer} onFocus={setFocusClip} onPick={onPick} />
        <div className="wf-pv-act">
          <WorkflowRunButton variant="icon" planId={planId} step="VIDEO" eventId={event.eventId} onDone={onActionDone} label={gen.label} why={gen.why} disabled={!gen.enabled} testId="wf-pv-gen">{gen.label}</WorkflowRunButton>
          {moreMenu ? (
            <Popover trigger="click" placement="bottomRight" content={moreMenu} title="Thêm" destroyTooltipOnHide={false}>
              <span><WfIconButton icon="ellipsis" label="Thêm: tải clip lên, cắt đầu/đuôi, chi tiết" testId="wf-pv-more-btn" /></span>
            </Popover>
          ) : null}
        </div>
        {ev ? <WorkflowGenerationEditor eventId={event.eventId} eventOrder={event.eventOrder} ev={ev} jobs={status.jobs} onActionDone={onActionDone} /> : null}
      </section>

      {drawer ? <WorkflowClipDetailDrawer open onClose={() => setDrawer(false)} status={status} planId={planId} storyId={storyId} eventId={event.eventId} order={event.eventOrder} ev={ev} detail={detail} onActionDone={onActionDone} /> : null}
    </div>
  );
};

export default WorkflowPromptVideoStep;
